import React from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import { FastImageProps } from 'react-native-fast-image'
import { useTheme } from '@ui-kitten/components'
import { Text } from '@components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ImageWithFullscreen } from '@src/components/Image/FastImageWithPlaceholder'
import {
  ImageRequestActionSheetOption,
  useImageRequestActionSheet,
} from '@src/hooks/useImageRequestActionSheet'
import { CircleIcon } from '@src/components'
import { IconButton } from '@src/components/base'
import { useUserInitials } from '@src/utils/hooks'
import { useIsDarkMode } from '@src/config/theme'
import { ReactNativeFile } from '@src/utils/image'

interface ProfilePhotoProps extends Omit<FastImageProps, 'style'> {
  style?: StyleProp<ViewStyle>
  source: { uri: string | undefined }
  onPhotoChange?: (photo: ReactNativeFile) => void
}

const SIZE = 80

export const ProfilePhoto = ({
  style,
  source,
  onPhotoChange = () => null,
  ...fastImageProps
}: ProfilePhotoProps) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const initialsAvatarBackgroundColor = isDarkMode
    ? theme['theme.accent.brick']
    : theme['theme.accent.citron']

  const { showImageRequestActionSheet } = useImageRequestActionSheet(onPhotoChange)
  const initials = useUserInitials()

  const onShowImageRequest = () =>
    showImageRequestActionSheet({
      options: [
        ImageRequestActionSheetOption.ChooseFromLibrary,
        ImageRequestActionSheetOption.TakePhoto,
      ],
    })

  const sizeStyles = { width: SIZE, height: SIZE, borderRadius: SIZE / 2 }

  const placeholderIcon = initials ? (
    <View
      style={[
        styles.initialsAvatarContainer,
        {
          backgroundColor: initialsAvatarBackgroundColor,
        },
      ]}
    >
      <Text type="title-1" style={styles.initials}>
        {initials}
      </Text>
    </View>
  ) : (
    <CircleIcon name="user" style={sizeStyles} iconStyle={{ width: SIZE / 2, height: SIZE / 2 }} />
  )

  return (
    <View style={[styles.container, style, sizeStyles]}>
      {source.uri ? (
        <ImageWithFullscreen
          resizeMode="cover"
          source={source}
          style={sizeStyles}
          placeholderComponent={placeholderIcon}
          {...fastImageProps}
        />
      ) : (
        placeholderIcon
      )}
      <IconButton
        type="primary"
        size="xs"
        iconName="plus"
        style={styles.uploadPhoto}
        onPress={onShowImageRequest}
        accessibilityLabel="Upload photo"
      />
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'theme.background',
  },
  initialsAvatarContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: SIZE,
    height: SIZE,
    borderRadius: 100,
  },
  initials: {
    color: 'theme.primary.dark',
  },
  circleIcon: {
    marginRight: 0,
  },
  uploadPhoto: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
})
